import dayjs from '@dayjs';
import {
  i18n,
} from '@i18n';
import {
  TParamDefinition,
} from '@qmulus/ui/composable/use-state-url.ts';
import {
  isNullish,
} from '@qmulus/ui/utils/common.ts';
import type {
  ManipulateType, OpUnitType, QUnitType,
} from 'dayjs';
import type {
  Duration,
} from 'dayjs/plugin/duration';
import {
  SafeParseReturnType,
  z,
} from 'zod';

import {
  formatDayJsDateWithoutTime,
  formatDayjsDate,
} from '@/utils/date-formats.ts';

export const I18N_PREFIX = 'components/date-range-picker/date-range-picker';

/* The earliest data we have data for */
export const MIN_YEAR = 1990;
/* Reasonable assumption of how far ahead our customers may want to plan. */
export const MAX_YEARS_INTO_FUTURE = 10;
export const MIN_ABS_DATE = dayjs().startOf('year').year(MIN_YEAR).toDate();
export const MAX_ABS_DATE = dayjs().endOf('year').add(MAX_YEARS_INTO_FUTURE, 'year').toDate();

export enum ECustomRangeType {
  // sorting is important. It determines the order of tabs in the date-range-picker component
  ABSOLUTE = 'absolute',
  RELATIVE = 'relative',
  CUSTOM = 'custom',
  BY_JOB = 'byJob',
}
export const CustomRangeTypeIcons: Record<ECustomRangeType, string> = {
  [ECustomRangeType.ABSOLUTE]: 'pi pi-arrows-h',
  [ECustomRangeType.CUSTOM]: 'pi pi-arrow-right-arrow-left',
  [ECustomRangeType.RELATIVE]: 'pi pi-arrow-left',
  [ECustomRangeType.BY_JOB]: 'pi pi-briefcase',
};

export interface IDateInterval {
  from: Date,
  to: Date,
}

/*
 * A custom range always has a dedicated from and to date.
 */
export interface IRangeCustom extends IDateInterval {
  rangeType: ECustomRangeType.CUSTOM,
}
export const RangeCustomZod: z.ZodType<IRangeCustom> = z.object({
  rangeType: z.literal(ECustomRangeType.CUSTOM),
  from: z.coerce.date(),
  to: z.coerce.date(),
});

const ALL_CUSTOM_RANGE: IRangeCustom = {
  rangeType: ECustomRangeType.CUSTOM,
  from: MIN_ABS_DATE,
  to: MAX_ABS_DATE,
};

export type TRelativeTimeUnit = Extract<(ManipulateType | 'quarters' | 'isoWeek'), 'years' | 'months' | 'weeks' | 'days' | 'minutes' | 'hours'>;
/**
 * A relative time range by default goes back into the past for a given amount of time.
 * To go into the future pass negative values.
 */
export interface IRangeRelative {
  rangeType: ECustomRangeType.RELATIVE,
  timeUnit: TRelativeTimeUnit,
  value: number,
}
export const RangeRelativeZod = z.object({
  rangeType: z.literal(ECustomRangeType.RELATIVE),
  timeUnit: z.enum([
    'years',
    'months',
    'weeks',
    'days',
    'minutes',
    'hours',
  ]),
  value: z.number(),
});

export type TAbsoluteTimeUnit = 'day' | 'isoWeek' | 'months' | 'quarters' | 'years';
export interface TRangeAbsolute {
  rangeType: ECustomRangeType.ABSOLUTE,
  timeUnit: TAbsoluteTimeUnit,
  year: number,
  value: number,
}
export const RangeAbsoluteZod = z.object({
  rangeType: z.literal(ECustomRangeType.ABSOLUTE),
  timeUnit: z.enum([
    'day',
    'isoWeek',
    'months',
    'quarters',
    'years',
  ]),
  year: z.number(),
  value: z.number(),
});

export const LAST_JOB_ID = 'lastJob' as const;

export interface IRangeByJob {
  rangeType: ECustomRangeType.BY_JOB,
  jobId: number | typeof LAST_JOB_ID,
  startedOrPlannedTimeAtUtc?: string,
  finishedOrPlannedTimeAtUtc?: string | null,
  previousJobId?: number | null,
  nextJobId?: number | null,
  jobName?: string,
}
export const RangeByJobZod = z.object({
  rangeType: z.literal(ECustomRangeType.BY_JOB),
  jobId: z.union([
    z.number(),
    z.literal(LAST_JOB_ID),
  ]),
  startedOrPlannedTimeAtUtc: z.string().datetime({
    offset: true,
  }).optional(),
  finishedOrPlannedTimeAtUtc: z.union([
    z.string().datetime({
      offset: true,
    }),
    z.null(),
  ]).optional(),
  previousJobId: z.union([
    z.number(),
    z.null(),
  ]).optional(),
  nextJobId: z.union([
    z.number(),
    z.null(),
  ]).optional(),
});

export type TRange = IRangeCustom | IRangeRelative | TRangeAbsolute | IRangeByJob;
export const RangeZod = z.union([
  RangeCustomZod,
  RangeRelativeZod,
  RangeAbsoluteZod,
  RangeByJobZod,
]);

export type TRangeLimits = {
  /* The absolute minimal Date that the user shall be able to select */
  minDate?: Date,
  /* The absolute maximal Date that the user shall be able to select */
  maxDate?: Date,
  /* Any range that exceeds the maxDuration will be hidden from the user */
  maxDuration?: Duration,
};

/**
 * Direction in which preset values are defined
 * "future" are negative values in order not to break backward compatibility
 * with existing links people might use
 */
export type TDatePickerDirection = 'past' | 'future' | 'mixed';

/* Some shared properties across many components and subcomponents */
export type TDatePickerProps = {
  modelValue: TRange | undefined,
  disabled?: boolean,
  direction?: TDatePickerDirection,
} & TRangeLimits;

export type TAbsoluteConfig = {
  /**
   * Any unit chosen here will be available for the user to choose in the menu.
   */
  allowedTimeUnitList: TAbsoluteTimeUnit[],
};

export type TDatePickerConfig = {
  /**
   * Any range type defined here will be available to be chosen in the custom menu.
   */
  allowedRangeTypes?: ECustomRangeType[],
  absolute?: TAbsoluteConfig,
};

export type TSkipButton = 'next' | 'previous';
export type TSkipButtonTranslations = Record<TSkipButton, string>;

export const DEFAULT_CONFIG: Required<TDatePickerConfig> = {
  allowedRangeTypes: [
    ECustomRangeType.CUSTOM,
    ECustomRangeType.ABSOLUTE,
    ECustomRangeType.RELATIVE,
    ECustomRangeType.BY_JOB,
  ],
  absolute: {
    allowedTimeUnitList: [
      'day',
      'isoWeek',
      'months',
      'quarters',
      'years',
    ],
  },
};

export const DATE_PICKER_PROPS_DEFAULTS: Record<keyof Omit<TDatePickerProps, 'modelValue'>, any> = {
  disabled: false,
  minDate: MIN_ABS_DATE,
  maxDate: MAX_ABS_DATE,
  maxDuration: undefined,
  direction: 'past',
};

/*
 * Converts any range definition into in actual date interval.
 */
export function rangeToDateInterval(definition: TRange): IDateInterval {
  switch (definition.rangeType) {
    case ECustomRangeType.CUSTOM:
      return {
        from: new Date(definition.from.getTime()),
        to: new Date(definition.to.getTime()),
      };

    case ECustomRangeType.RELATIVE: {
      // "future" has negative value
      return definition.value > 0
        ? {
          from: dayjs().subtract(definition.value, definition.timeUnit as ManipulateType).toDate(),
          to: new Date(),
        }
        : {
          from: new Date(),
          to: dayjs().subtract(definition.value, definition.timeUnit as ManipulateType).toDate(),
        };
    }

    case ECustomRangeType.ABSOLUTE:
      // eslint-disable-next-line no-case-declarations
      let pointer = dayjs().year(definition.year);
      switch (definition.timeUnit) {
        case 'years':
          break;
        case 'months':
          pointer = pointer.month(definition.value);
          break;
        case 'quarters':
          pointer = pointer.quarter(definition.value);
          break;
        case 'isoWeek':
          pointer = pointer.isoWeek(definition.value);
          break;
        case 'day':
          pointer = pointer.dayOfYear(definition.value);
          break;
        default:
          throw new Error(
            // @ts-expect-error:: default may never be reached. But we want to keep it
            // in case we forget to add a case when we expand the possible cases
            `Unsupported timeUnit '${definition.timeUnit}' for absolute rangeType
              '${ECustomRangeType.ABSOLUTE}'.`,
          );
      }
      return {
        from: pointer.startOf(definition.timeUnit as (QUnitType | OpUnitType)).toDate(),
        // we add one millisecond to include the begin of the next day. Otherwise we get 23:59:59.999
        to: pointer.endOf(definition.timeUnit as (QUnitType | OpUnitType)).add(1, 'millisecond').toDate(),
      };

    case ECustomRangeType.BY_JOB:
      return {
        from: definition.startedOrPlannedTimeAtUtc
          ? new Date(definition.startedOrPlannedTimeAtUtc)
          : dayjs().subtract(1, 'day').toDate(),
        to: definition.finishedOrPlannedTimeAtUtc
          ? new Date(definition.finishedOrPlannedTimeAtUtc)
          : new Date(),
      };

    default:
      // @ts-expect-error:: default may never be reached. But we want to keep it
      // in case we forget to add a case when we expand the possible cases
      throw new Error(`Unsupported rangeType '${definition.rangeType}'.`);
  }
}

/*
 * Converts a range to the duration in seconds
 */
export function rangeToDurationMs(range: TRange): number {
  const interval = rangeToDateInterval(range);
  return interval.to.getTime() - interval.from.getTime();
}

export function isIntervalForRangeIdAll(interval: IDateInterval): boolean {
  return interval.from.toISOString() === ALL_CUSTOM_RANGE.from.toISOString()
    && interval.to.toISOString() === ALL_CUSTOM_RANGE.to.toISOString();
}

/*
 * Creates a human friendly display name for the range
 */
export function rangeToLabel(range: TRange, short?: boolean): string {
  const dateInterval = rangeToDateInterval(range);
  const dayJsInterval = {
    from: dayjs(dateInterval.from),
    to: dayjs(dateInterval.to),
  };

  switch (range.rangeType) {
    case ECustomRangeType.CUSTOM:
      if (isIntervalForRangeIdAll(dateInterval)) {
        return i18n.global.t(`${I18N_PREFIX}.all`);
      }
      return `${formatDayjsDate(dayJsInterval.from)} - ${formatDayjsDate(dayJsInterval.to)}`;
    case ECustomRangeType.RELATIVE:
      return [
        // negative values = future
        range.value < 0 ? '+' : '',
        Math.abs(range.value),
        short
          ? i18n.global.t(`${I18N_PREFIX}.timeUnitsShort.${range.timeUnit}`)
          : i18n.global.t(`${I18N_PREFIX}.timeUnits.${range.timeUnit}`, range.value),
      ].join(short ? '' : ' ');
    case ECustomRangeType.ABSOLUTE:
      switch (range.timeUnit) {
        case 'years':
          // use value instead year, to allow consistent relative offset
          return range.value.toString();
        case 'months':
          return `${dayJsInterval.from.format('MMM')} ${dayJsInterval.from.year()}`;
        case 'quarters':
          return `Q${dayJsInterval.from.quarter()} ${dayJsInterval.from.year()}`;
        case 'isoWeek':
          // To determine if the week is the current of the last, we need to calculate
          // the absolute difference in weeks including changes in year.
          switch ((range.value - dayjs().isoWeek()) + 52 * (range.year - dayjs().year())) {
            case -1:
              return `${i18n.global.t(`${I18N_PREFIX}.calendarWeek`)}${dayJsInterval.from.isoWeek()} (${i18n.global.t(`${I18N_PREFIX}.previousWeek`)})`;
            case 0:
              return `${i18n.global.t(`${I18N_PREFIX}.calendarWeek`)}${dayJsInterval.from.isoWeek()} (${i18n.global.t(`${I18N_PREFIX}.currentWeek`)})`;
            case 1:
              return `${i18n.global.t(`${I18N_PREFIX}.calendarWeek`)}${dayJsInterval.from.isoWeek()} (${i18n.global.t(`${I18N_PREFIX}.nextWeek`)})`;
            default:
              // eslint-disable-next-line no-case-declarations
              let label = `${i18n.global.t(`${I18N_PREFIX}.calendarWeek`)}${dayJsInterval.from.isoWeek()}`;
              // eslint-disable-next-line no-case-declarations
              const currentYear = dayjs().year();
              // Both checks are required due to potential timezone differences.
              if (dayJsInterval.from.year() !== currentYear || dayJsInterval.to.year() !== currentYear) {
                label = `${dayJsInterval.from.year()} ${label}`;
              }
              return label;
          }
        case 'day':
          return formatDayJsDateWithoutTime(dayJsInterval.from);
        default:
          throw new Error(
            // @ts-expect-error:: default may never be reached. But we want to keep it
            // in case we forget to add a case when we expand the possible cases
            `Unsupported timeUnit '${range.timeUnit}' for absolute rangeType
              '${ECustomRangeType.ABSOLUTE}'.`,
          );
      }
    case ECustomRangeType.BY_JOB:
      return `${i18n.global.t(`${I18N_PREFIX}.jobLabel`)} "${range.jobName ?? range.jobId}"`;
    default:
      // @ts-expect-error:: default may never be reached. But we want to keep it
      // in case we forget to add a case when we expand the possible cases
      throw new Error(`Unsupported rangeType '${range.rangeType}'.`);
  }
}

// enum for reusing same IDs on different pages
export enum ERangeId {
  ALL = 'all',
  CURRENT_WEEK = 'currentWeek',

  PAST_MINUTE_30 = '-30min',
  PAST_HOUR_1 = '-1h',
  PAST_HOUR_2 = '-2h',
  PAST_HOUR_4 = '-4h',
  PAST_HOUR_8 = '-8h',
  PAST_HOUR_12 = '-12h',
  PAST_HOUR_24 = '-24h',
  PAST_DAY_1 = '-1d',
  PAST_DAY_2 = '-2d',
  PAST_DAY_3 = '-3d',
  PAST_DAY_7 = '-7d',
  PAST_WEEK_1 = '-1w',
  PAST_MONTH_1 = '-1m',
  PAST_MONTH_3 = '-3m',
  PAST_MONTH_6 = '-6m',
  PAST_YEAR_1 = '-1y',
  PAST_YEAR_2 = '-2y',
  PAST_YEAR_5 = '-5y',
  PAST_YEAR_10 = '-10y',

  FUTURE_MINUTE_30 = '+30min',
  FUTURE_HOUR_1 = '+1h',
  FUTURE_HOUR_2 = '+2h',
  FUTURE_HOUR_4 = '+4h',
  FUTURE_HOUR_8 = '+8h',
  FUTURE_HOUR_12 = '+12h',
  FUTURE_HOUR_24 = '+24h',
  FUTURE_DAY_1 = '+1d',
  FUTURE_DAY_2 = '+2d',
  FUTURE_DAY_3 = '+3d',
  FUTURE_DAY_7 = '+7d',
  FUTURE_WEEK_1 = '+1w',
  FUTURE_MONTH_1 = '+1m',
  FUTURE_MONTH_3 = '+3m',
  FUTURE_MONTH_6 = '+6m',
  FUTURE_YEAR_1 = '+1y',
  FUTURE_YEAR_2 = '+2y',
  FUTURE_YEAR_5 = '+5y',
  FUTURE_YEAR_10 = '+10y',
}

export function rangeIdToRange(rangeId: ERangeId): TRange {
  switch (rangeId) {
    case ERangeId.CURRENT_WEEK:
      return {
        rangeType: ECustomRangeType.ABSOLUTE,
        timeUnit: 'isoWeek',
        year: dayjs().year(),
        value: dayjs().isoWeek(),
      };
    case ERangeId.PAST_MINUTE_30:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'minutes',
        value: 30,
      };
    case ERangeId.PAST_HOUR_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: 1,
      };
    case ERangeId.PAST_HOUR_2:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: 2,
      };
    case ERangeId.PAST_HOUR_4:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: 4,
      };
    case ERangeId.PAST_HOUR_8:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: 8,
      };
    case ERangeId.PAST_HOUR_12:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: 12,
      };
    case ERangeId.PAST_HOUR_24:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: 24,
      };
    case ERangeId.PAST_DAY_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'days',
        value: 1,
      };
    case ERangeId.PAST_DAY_2:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'days',
        value: 2,
      };
    case ERangeId.PAST_DAY_3:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'days',
        value: 3,
      };
    case ERangeId.PAST_DAY_7:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'days',
        value: 7,
      };
    case ERangeId.PAST_WEEK_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'weeks',
        value: 1,
      };
    case ERangeId.PAST_MONTH_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'months',
        value: 1,
      };
    case ERangeId.PAST_MONTH_3:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'months',
        value: 3,
      };
    case ERangeId.PAST_MONTH_6:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'months',
        value: 6,
      };
    case ERangeId.PAST_YEAR_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'years',
        value: 1,
      };
    case ERangeId.PAST_YEAR_2:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'years',
        value: 2,
      };
    case ERangeId.PAST_YEAR_5:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'years',
        value: 5,
      };
    case ERangeId.PAST_YEAR_10:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'years',
        value: 10,
      };
    case ERangeId.FUTURE_MINUTE_30:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'minutes',
        value: -30,
      };
    case ERangeId.FUTURE_HOUR_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: -1,
      };
    case ERangeId.FUTURE_HOUR_2:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: -2,
      };
    case ERangeId.FUTURE_HOUR_4:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: -4,
      };
    case ERangeId.FUTURE_HOUR_8:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: -8,
      };
    case ERangeId.FUTURE_HOUR_12:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: -12,
      };
    case ERangeId.FUTURE_HOUR_24:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'hours',
        value: -24,
      };
    case ERangeId.FUTURE_DAY_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'days',
        value: -1,
      };
    case ERangeId.FUTURE_DAY_2:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'days',
        value: -2,
      };
    case ERangeId.FUTURE_DAY_3:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'days',
        value: -3,
      };
    case ERangeId.FUTURE_DAY_7:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'days',
        value: -7,
      };
    case ERangeId.FUTURE_WEEK_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'weeks',
        value: -1,
      };
    case ERangeId.FUTURE_MONTH_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'months',
        value: -1,
      };
    case ERangeId.FUTURE_MONTH_3:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'months',
        value: -3,
      };
    case ERangeId.FUTURE_MONTH_6:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'months',
        value: -6,
      };
    case ERangeId.FUTURE_YEAR_1:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'years',
        value: -1,
      };
    case ERangeId.FUTURE_YEAR_2:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'years',
        value: -2,
      };
    case ERangeId.FUTURE_YEAR_5:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'years',
        value: -5,
      };
    case ERangeId.FUTURE_YEAR_10:
      return {
        rangeType: ECustomRangeType.RELATIVE,
        timeUnit: 'years',
        value: -10,
      };
    case ERangeId.ALL:
      return ALL_CUSTOM_RANGE;
    default:
      throw new Error(`Unsupported ECustomRangeType value '${rangeId}'.`);
  }
}

export const DEFAULT_RANGES_LIST: ERangeId[] = [
  ERangeId.CURRENT_WEEK,
  ERangeId.PAST_DAY_7,
  ERangeId.PAST_MONTH_1,
  ERangeId.PAST_MONTH_3,
  ERangeId.PAST_YEAR_1,
];

export const DEFAULT_RANGES_LIST_BY_DIRECTION: Record<TDatePickerDirection, ERangeId[]> = {
  past: DEFAULT_RANGES_LIST,
  future: [
    ERangeId.CURRENT_WEEK,
    ERangeId.FUTURE_DAY_7,
    ERangeId.FUTURE_MONTH_1,
    ERangeId.FUTURE_MONTH_3,
    ERangeId.FUTURE_YEAR_1,
  ],
  mixed: [
    ERangeId.PAST_MONTH_1,
    ERangeId.PAST_DAY_7,
    ERangeId.CURRENT_WEEK,
    ERangeId.FUTURE_DAY_7,
    ERangeId.FUTURE_MONTH_1,
  ],
};

export const DEFAULT_RANGE: TRange = rangeIdToRange(DEFAULT_RANGES_LIST[0]);

export type TChoice = TRange | ERangeId;

/**
 * Return True if the given range is smaller than the maxDuration.
 * If the maxDuration is undefined the range is always considered valid unless
 * it's undefined too.
 */
export function isRangeValid(range: TRange | undefined, limits?: TRangeLimits): boolean {
  if (range === undefined) {
    return false;
  }
  if (limits === undefined) {
    return true;
  }
  if (limits.maxDuration !== undefined && rangeToDurationMs(range) > limits.maxDuration.asMilliseconds()) {
    return false;
  }

  const interval = rangeToDateInterval(range);
  if (limits.minDate !== undefined && interval.from < limits.minDate) {
    return false;
  }
  return !(limits.maxDate !== undefined && interval.to > limits.maxDate);
}

export function choicesToRanges(choices: TChoice[], limits?: TRangeLimits): TRange[] {
  const ranges = choices.map((choice) => (typeof choice === 'string' ? rangeIdToRange(choice) : choice));
  if (limits === undefined) {
    return ranges;
  }
  return ranges.filter((range) => isRangeValid(range, limits));
}

export function relativeRangeToDuration(range: IRangeRelative): Duration {
  return dayjs.duration(range.value, range.timeUnit);
}

export function durationToPastRelativeRange(duration: Duration): IRangeRelative {
  let timeUnit: TRelativeTimeUnit = 'hours';
  let value: number = 0;

  if (duration.asMinutes() < 60) {
    timeUnit = 'minutes';
    value = Math.round(duration.asMinutes());
  } else if (duration.asHours() < 24) {
    timeUnit = 'hours';
    value = Math.round(duration.asHours());
  } else if (duration.asDays() < 7) {
    timeUnit = 'days';
    value = Math.round(duration.asDays());
  } else if (duration.asWeeks() < 4) {
    timeUnit = 'weeks';
    value = Math.round(duration.asWeeks());
  } else if (duration.asMonths() < 12) {
    timeUnit = 'months';
    value = Math.round(duration.asMonths());
  } else {
    timeUnit = 'years';
    value = Math.round(duration.asYears());
  }

  return {
    rangeType: ECustomRangeType.RELATIVE,
    timeUnit,
    value,
  };
}

export type TSkipFrame = 'full' | 'half' | 'quarter';
export const SKIP_FRAME_TO_FACTOR: Record<TSkipFrame, number> = {
  full: 1,
  half: 0.5,
  quarter: 0.25,
};

/**
 * Builds the range that comes before the given range.
 *
 * For absolute ranges with `frame = 'full'`, the result preserves the absolute range type and
 * simply decrements the value (and year when using 'years' as time unit).
 *
 * For all other combinations — including non-full frames on absolute ranges and all
 * relative/custom ranges — the result is a custom range whose window has the same duration as the
 * input range and is shifted backward by `duration * SKIP_FRAME_TO_FACTOR[frame]`.
 *
 * @param range - The current range to navigate backward from.
 * @param frame - How far to shift: `'full'` (one full window), `'half'` (50 %), or `'quarter'` (25 %).
 */
export function buildPreviousRange(range: TRange, frame: TSkipFrame = 'full'): TRange {
  if (range.rangeType === ECustomRangeType.ABSOLUTE && frame === 'full') {
    return {
      rangeType: range.rangeType,
      timeUnit: range.timeUnit,
      // Thankfully dayjs handles all overflows. So we don't have to
      year: range.year - (range.timeUnit === 'years' ? 1 : 0),
      value: range.value - 1,
    };
  }

  const interval = rangeToDateInterval(range);
  const fromDayJs = dayjs(interval.from);
  const toDayJs = dayjs(interval.to);
  const durationMs = toDayJs.diff(fromDayJs, 'milliseconds');
  const shiftMs = Math.round(durationMs * SKIP_FRAME_TO_FACTOR[frame]);

  return {
    rangeType: ECustomRangeType.CUSTOM,
    from: fromDayJs.subtract(shiftMs, 'milliseconds').toDate(),
    to: toDayJs.subtract(shiftMs, 'milliseconds').toDate(),
  };
}

/**
 * Builds the range that comes after the given range.
 *
 * For absolute ranges with `frame = 'full'`, the result preserves the absolute range type and
 * simply increments the value (and year when using 'years' as time unit).
 *
 * For all other combinations — including non-full frames on absolute ranges and all
 * relative/custom ranges — the result is a custom range whose window has the same duration as the
 * input range and is shifted forward by `duration * SKIP_FRAME_TO_FACTOR[frame]`.
 *
 * @param range - The current range to navigate forward from.
 * @param frame - How far to shift: `'full'` (one full window), `'half'` (50 %), or `'quarter'` (25 %).
 */
export function buildNextRange(range: TRange, frame: TSkipFrame = 'full'): TRange {
  if (range.rangeType === ECustomRangeType.ABSOLUTE && frame === 'full') {
    return {
      rangeType: range.rangeType,
      timeUnit: range.timeUnit,
      // Thankfully dayjs handles all overflows. So we don't have to
      year: range.year + (range.timeUnit === 'years' ? 1 : 0),
      value: range.value + 1,
    };
  }

  const interval = rangeToDateInterval(range);
  const fromDayJs = dayjs(interval.from);
  const toDayJs = dayjs(interval.to);
  const durationMs = toDayJs.diff(fromDayJs, 'milliseconds');
  const shiftMs = Math.round(durationMs * SKIP_FRAME_TO_FACTOR[frame]);

  return {
    rangeType: ECustomRangeType.CUSTOM,
    from: fromDayJs.add(shiftMs, 'milliseconds').toDate(),
    to: toDayJs.add(shiftMs, 'milliseconds').toDate(),
  };
}

function tryParseLegacyDateRangePickerQuery(queryValue: string): TRange | undefined {
  if (!queryValue.includes('{')) {
    return undefined;
  }

  try {
    const parsed = RangeZod.safeParse(JSON.parse(queryValue));
    if (parsed.success) {
      return parsed.data;
    }
  } finally {
    // ignore any error.
  }
  return undefined;
}

// `::` was chosen because it does not require to be encoded in the URL and does
// not conflict with any of the potential string representations of the range,
// while keeping the value nice and readable.
export const QUERY_SEP = '::';
export const LAST_JOB_RANGE_QUERY = `${ECustomRangeType.BY_JOB}${QUERY_SEP}${LAST_JOB_ID}` as const;

export function parseDateRangePickerRangeQuery(queryValue: string): TRange | undefined {
  const paramParts = String(queryValue).split(QUERY_SEP);

  let parsed: SafeParseReturnType<TRange, TRange> | undefined = undefined;

  if (paramParts.length < 2) {
    const potentialLegacyQueryParam = tryParseLegacyDateRangePickerQuery(queryValue);
    if (!isNullish(potentialLegacyQueryParam)) {
      return potentialLegacyQueryParam;
    }

    console.warn(`Encountered invalid dateRangePickerParam: ${queryValue}. Ignoring.`);
    return undefined;
  }

  if (paramParts[0] === ECustomRangeType.CUSTOM) {
    parsed = RangeCustomZod.safeParse({
      rangeType: ECustomRangeType.CUSTOM,
      from: paramParts[1],
      to: paramParts[2],
    });
  }

  if (paramParts[0] === ECustomRangeType.RELATIVE) {
    parsed = RangeRelativeZod.safeParse({
      rangeType: ECustomRangeType.RELATIVE,
      timeUnit: paramParts[1],
      value: Number(paramParts[2]),
    });
  }

  if (paramParts[0] === ECustomRangeType.ABSOLUTE && paramParts.length >= 4) {
    parsed = RangeAbsoluteZod.safeParse({
      rangeType: ECustomRangeType.ABSOLUTE,
      timeUnit: paramParts[1],
      year: Number(paramParts[2]),
      value: Number(paramParts[3]),
    });
  }

  if (paramParts[0] === ECustomRangeType.BY_JOB && paramParts.length >= 2) {
    parsed = RangeByJobZod.safeParse({
      rangeType: ECustomRangeType.BY_JOB,
      jobId: paramParts[1] === LAST_JOB_ID
        ? LAST_JOB_ID
        : Number(paramParts[1]),
    });
  }

  if (parsed === undefined || !parsed.success) {
    console.warn(`Encountered invalid dateRangePickerParam: ${queryValue}. Ignoring.`);
    return undefined;
  }
  return parsed.data;
}

export function serializeDateRangePickerRange(range: TRange): string {
  const parts: string[] = [
    range.rangeType,
  ];
  switch (range.rangeType) {
    case ECustomRangeType.CUSTOM: {
      parts.push(range.from.toISOString(), range.to.toISOString());
      break;
    }
    case ECustomRangeType.RELATIVE: {
      parts.push(range.timeUnit, range.value.toString());
      break;
    }
    case ECustomRangeType.ABSOLUTE: {
      parts.push(range.timeUnit, range.year.toString(), range.value.toString());
      break;
    }
    case ECustomRangeType.BY_JOB: {
      parts.push(range.jobId.toString());
      break;
    }
  }

  return parts.join(QUERY_SEP);
}

export function dateRangePickerRangeParam(defaultValue: TRange): TParamDefinition<TRange> {
  return {
    default: defaultValue,
    parse: (v) => parseDateRangePickerRangeQuery(String(v)) ?? defaultValue,
    serialize: (v) => v !== undefined ? serializeDateRangePickerRange(v) : undefined,
  };
}
